import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "../lib/adminApi";
import type {
  ActionResponse,
  LotListResponse,
  LotOverrideResponse,
  NotificationsDto,
  SettingsResponse,
  TestNotificationResponse,
  ThresholdsDto,
  UpsertLotOverrideRequest,
} from "../types/admin";

/**
 * 관리자 화면의 서버 상태 훅.
 *
 * 조회는 자동 재폴링하지 않는다 — 운영자가 값을 입력하는 화면에서 배경 갱신이 일어나면
 * 편집 중인 폼이 예고 없이 흔들린다. 변경 후에는 해당 쿼리만 명시적으로 무효화한다.
 */

const SETTINGS_KEY = ["admin", "settings"] as const;
const LOTS_KEY = ["admin", "lots"] as const;

export function useAdminSettings(token: string | null) {
  return useQuery<SettingsResponse>({
    queryKey: SETTINGS_KEY,
    queryFn: ({ signal }) =>
      adminApi.get<SettingsResponse>("/api/admin/settings", token, signal),
    enabled: !!token,
    staleTime: 30_000,
    // 로그인 실패/만료는 재시도해도 같은 결과다. 네트워크 순단만 한 번 더 본다.
    retry: 1,
  });
}

export function useAdminLots(token: string | null) {
  return useQuery<LotListResponse>({
    queryKey: LOTS_KEY,
    queryFn: ({ signal }) =>
      adminApi.get<LotListResponse>("/api/admin/lots", token, signal),
    enabled: !!token,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useUpdateThresholds(token: string | null) {
  const qc = useQueryClient();
  return useMutation<ThresholdsDto, Error, ThresholdsDto>({
    mutationFn: (body) =>
      adminApi.put<ThresholdsDto>("/api/admin/settings/thresholds", token, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      // 전역값이 바뀌면 각 lot 의 '적용 중' 값도 달라진다.
      void qc.invalidateQueries({ queryKey: LOTS_KEY });
    },
  });
}

export function useResetThresholds(token: string | null) {
  const qc = useQueryClient();
  return useMutation<ThresholdsDto, Error, void>({
    mutationFn: () =>
      adminApi.post<ThresholdsDto>("/api/admin/settings/thresholds/reset", token),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      void qc.invalidateQueries({ queryKey: LOTS_KEY });
    },
  });
}

export function useUpdateNotifications(token: string | null) {
  const qc = useQueryClient();
  return useMutation<
    NotificationsDto,
    Error,
    { enabled: boolean; webhookUrl: string; dashboardUrl: string }
  >({
    mutationFn: (body) =>
      adminApi.put<NotificationsDto>("/api/admin/settings/notifications", token, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: SETTINGS_KEY }),
  });
}

export function useTestNotification(token: string | null) {
  return useMutation<TestNotificationResponse, Error, void>({
    mutationFn: () =>
      adminApi.post<TestNotificationResponse>(
        "/api/admin/settings/notifications/test",
        token,
      ),
  });
}

export function useUpsertLotOverride(token: string | null) {
  const qc = useQueryClient();
  return useMutation<
    LotOverrideResponse,
    Error,
    { siteKey: string; lotId: string; body: UpsertLotOverrideRequest }
  >({
    mutationFn: ({ siteKey, lotId, body }) =>
      adminApi.put<LotOverrideResponse>(
        `/api/admin/lots/${encodeURIComponent(siteKey)}/${encodeURIComponent(lotId)}`,
        token,
        body,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: LOTS_KEY }),
  });
}

export function useDeleteLotOverride(token: string | null) {
  const qc = useQueryClient();
  return useMutation<ActionResponse, Error, { siteKey: string; lotId: string }>({
    mutationFn: ({ siteKey, lotId }) =>
      adminApi.del<ActionResponse>(
        `/api/admin/lots/${encodeURIComponent(siteKey)}/${encodeURIComponent(lotId)}`,
        token,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: LOTS_KEY }),
  });
}

export function useAdminAction(token: string | null) {
  const qc = useQueryClient();
  return useMutation<ActionResponse, Error, "poll" | "cache-clear">({
    mutationFn: (action) =>
      adminApi.post<ActionResponse>(`/api/admin/actions/${action}`, token),
    onSuccess: () => {
      // 폴링/캐시 조작은 공개 status 응답과 lot 목록을 모두 바꾼다.
      void qc.invalidateQueries({ queryKey: ["status"] });
      void qc.invalidateQueries({ queryKey: LOTS_KEY });
    },
  });
}
